import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlayIcon } from 'lucide-react';
import { getProductBySlug } from '../data/products';
import { ProductSpecs } from '../components/products/ProductSpecs';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useTranslation } from '../lib/i18n';
import { formatEur } from '../lib/format';
import { trackProductViewed, trackProductSpecsViewed, trackProductGalleryViewed } from '../lib/pixel';
// Fires a tracking callback once when the element scrolls into view.
function useViewedOnce(enabled: boolean, onView: () => void): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || firedRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onView();
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.3
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return ref;
}
interface ProductDetailPageProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductDetailPage({
  onRequestQuote
}: ProductDetailPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const [activeMedia, setActiveMedia] = useState<{
    type: 'image' | 'video';
    url: string;
  } | null>(null);
  const productCtx = {
    product_name: product?.name,
    product_category: product?.series
  };
  // ProductViewed once per product page load.
  useEffect(() => {
    if (product) trackProductViewed(productCtx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);
  // ProductSpecsViewed / ProductGalleryViewed when those sections scroll in.
  const specsRef = useViewedOnce(!!product, () => trackProductSpecsViewed(productCtx));
  const galleryRef = useViewedOnce(!!(product && product.gallery && product.gallery.length > 0), () => trackProductGalleryViewed(productCtx));
  if (!product) {
    return <Navigate to="/products" replace data-id="element-960" data-mp="2k31bujkhmowf" />;
  }
  const hasGallery = product.gallery && product.gallery.length > 0;
  return <main className="min-h-screen bg-dark-bg" data-id="element-961" data-mp="2o43frdpcnp4q">
      {/* Back Link */}
      <div className="bg-dark-surface border-b-2 border-dark-border" data-id="element-962" data-mp="2t4r1bjut7drk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-963" data-mp="tsydodtwvkri">
          <Link to="/products" className="inline-flex items-center text-gray-400 hover:text-brand-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow rounded-industrial" data-id="element-964" data-mp="yr9oaqs15zas">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-965" data-mp="mfz4lh5x2jyc" />
            {en ? 'Back to products' : ru ? 'Назад к продуктам' : 'Nazad na proizvode'}
          </Link>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-dark-surface border-b-2 border-dark-border" data-id="element-966" data-mp="1a6couli9feid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-967" data-mp="1a48ky3kd2fi3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-968" data-mp="1581em9o1z1zm">
            <div className="space-y-4" data-id="element-969" data-mp="107dp5sfh2t7i">
              <div className="w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-8 flex items-center justify-center min-h-[400px] lg:min-h-[500px] relative" data-id="element-970" data-mp="10ahyt5bpih5k">
                <img src={product.image} alt={`${product.name} ${product.model}`} className="w-full h-auto object-contain max-h-[450px]" data-id="element-971" data-mp="3l07dniceu2y9" />
              </div>

              {/* Gallery Thumbnails */}
              {hasGallery && <div className="mt-6" ref={galleryRef} data-id="element-972" data-mp="1dgdwg78ed6yu">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-973" data-mp="yqoptwhjcar6">
                    {en ? 'Gallery' : ru ? 'Галерея' : 'Galerija'}
                  </h3>
                  <div className="grid grid-cols-4 gap-4" data-id="element-974" data-mp="13n6r2mdnpqyr">
                    {product.gallery?.map((media, idx) => <button key={idx} onClick={() => setActiveMedia(media)} className="relative aspect-square bg-dark-bg border-2 border-dark-border rounded-industrial overflow-hidden hover:border-brand-yellow transition-colors group" data-id="element-975" data-mp="1wdovbqq49ydr">
                        {media.type === 'image' ? <img src={media.url} alt="Gallery item" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-976" data-mp="b0m3lx0axz0h" /> : <div className="w-full h-full flex items-center justify-center bg-gray-900 group-hover:bg-gray-800 transition-colors" data-id="element-977" data-mp="df928mpfsf5b">
                            <PlayIcon className="w-8 h-8 text-brand-yellow" data-id="element-978" data-mp="8h7hzyg9y9s9" />
                          </div>}
                      </button>)}
                  </div>
                </div>}
            </div>

            <div className="pt-4" data-id="element-979" data-mp="3qliy4r75fmbd">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2" data-id="element-980" data-mp="3qmkl9u1fvz9g">
                {product.name}
              </h1>
              <p className="text-2xl text-gray-400 mb-6" data-id="element-981" data-mp="3lt7y7oces0it">{product.model}</p>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-982" data-mp="2y3x1chy6iq9y">
                {en ? product.descriptionEn || product.description : ru ? product.descriptionRu || product.description : product.description}
              </p>
              <div className="mb-8" data-id="element-983" data-mp="3mub468alizrn">
                {product.originalPrice && <div className="mb-1" data-id="element-984" data-mp="3rne1mrihutd4">
                    <span className="text-2xl text-gray-500 line-through" data-id="element-985" data-mp="gjmxquv9qdk">
                      €{formatEur(product.originalPrice)}
                    </span>
                  </div>}
                <div className="flex items-baseline gap-3" data-id="element-986" data-mp="33x9wugtkknkj">
                  <span className="text-5xl font-black text-brand-yellow" data-id="element-987" data-mp="33xjrihpk7iwk">
                    €{formatEur(product.price)}
                  </span>
                  <span className="text-base text-gray-400" data-id="element-988" data-mp="3dv66f46uzagj">
                    {en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-989" data-mp="o8i0yilp9zue">
                  {en ? 'incl. VAT' : ru ? 'с НДС' : 'Sa PDV'}: €
                  {formatEur(product.price * 1.2)} ·
                  {en ? ' Availability on request.' : ru ? ' Наличие по запросу.' : ' Dostupnost na upit.'}
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-990" data-mp="1yyd25dvwf1b7">
                {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-dark-bg" data-id="element-991" data-mp="1g0am3olbwepj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-992" data-mp="1g1lobjz4leej">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-993" data-mp="3fbjz7yywolll">
            {en ? 'Key features' : ru ? 'Ключевые особенности' : 'Ključne karakteristike'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-994" data-mp="2j8xz82bfkiud">
            {(en && product.highlightsEn ? product.highlightsEn : ru && product.highlightsRu ? product.highlightsRu : product.highlights).map(highlight => <div key={highlight} className="flex items-center space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-4" data-id="element-995" data-mp="2efd0u95xdmun">
                <CheckIcon className="w-6 h-6 text-brand-yellow flex-shrink-0" data-id="element-996" data-mp="25i2oslla9too" />
                <span className="text-white font-semibold" data-id="element-997" data-mp="1ribf2x1suzgh">{highlight}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-12 bg-dark-surface" ref={specsRef} data-id="element-998" data-mp="3lw2jni59ut52">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-999" data-mp="3pwcqex0w1nkz">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-1000" data-mp="3puigsev2hhw7">
            {en ? 'Technical specifications' : ru ? 'Технические характеристики' : 'Tehničke specifikacije'}
          </h2>
          <ProductSpecs specs={product.specs} data-id="element-1001" data-mp="1nzcbogh35r6a" />
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-dark-bg" data-id="element-1002" data-mp="1j841hi7xm1s2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1003" data-mp="1j71915uynra1">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-1004" data-mp="1xw7a6zdczrkk">
            {en ? 'Features and benefits' : ru ? 'Функции и преимущества' : 'Funkcije i prednosti'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1005" data-mp="26vuafhucqrc1">
            {(en && product.featuresEn ? product.featuresEn : ru && product.featuresRu ? product.featuresRu : product.features).map(feature => <div key={feature} className="flex items-start space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-6" data-id="element-1006" data-mp="7mngb7jifdb7">
                <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-1007" data-mp="qhk4ochkpa09" />
                <span className="text-gray-300" data-id="element-1008" data-mp="mib9r4asr634">{feature}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-surface border-t-2 border-dark-border" data-id="element-1009" data-mp="1e97ns88lfgrz">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1010" data-mp="1a72vpdxsms7g">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-1011" data-mp="1a97aws157dn1">
            {en ? 'Ready to order?' : ru ? 'Готовы заказать?' : 'Spremni za poručivanje?'}
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-1012" data-mp="1xwf3c9dh2s7n">
            {en ? 'Contact us for a detailed quote and lead time.' : ru ? 'Свяжитесь с нами для получения подробного предложения и сроков поставки.' : 'Kontaktirajte nas za detaljnu ponudu i rok isporuke.'}
          </p>
          <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-1013" data-mp="25h96vr4ds7u1">
            {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
          </Button>
        </div>
      </section>

      {/* Media Modal */}
      <Modal isOpen={!!activeMedia} onClose={() => setActiveMedia(null)} title={product.name} data-id="element-1014" data-mp="2of2bcxunkptj">
        <div className="bg-dark-bg rounded-industrial overflow-hidden flex items-center justify-center min-h-[50vh]" data-id="element-1015" data-mp="2t58zyxp60abs">
          {activeMedia?.type === 'image' ? <img src={activeMedia.url} alt="Gallery full" className="max-w-full max-h-[80vh] object-contain" data-id="element-1016" data-mp="2y6ohifs4vypf" /> : activeMedia?.type === 'video' ? <video src={activeMedia.url} controls autoPlay className="max-w-full max-h-[80vh]" data-id="element-1017" data-mp="173agud3jhme0" /> : null}
        </div>
      </Modal>
    </main>;
}